import {
  useWSSnackbar,
  WSActions,
  WSControl,
  WSDivider,
  WSForm,
  WSInfoBox,
  WSInputText,
  WSList,
  WSModal,
  WSModalCustomProps
} from "@wingspanhq/fe-component-library";
import uniqBy from "lodash/uniqBy";
import {
  AttributeDataType,
  CustomFieldResourceType,
  Engagement,
  EngagementStatus,
  Payee,
  PayeeEngagementType
} from "@wingspanhq/wingspan-api-sdk";
import React, { useState } from "react";
import * as Yup from "yup";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import { queriesCustomFields, queriesEngagements } from "@query";
import { WSQueries } from "../../../../query/WSQuery";
import { validatorEmail } from "../../../../shared/validators/validatorEmail";
import {
  contactCompanyValidator,
  contactNameValidator
} from "../../../../utils/validators";
import {
  getValidationSchemaCustomFields,
  getFormDataCustomFields
} from "../../../../shared/components/FormPartialCustomFields";
import { selectorEngagementOptions } from "../../../EngagementTypes/selectors/selectorEngagementOptions";
import { selectorPayeeCustomFieldsRequest } from "../../selectors/selectorPayeeCustomFieldsRequest";
import {
  FormPartialPayeeQboMapping,
  PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES,
  PayeeQboMappingFormValues
} from "../../../Integrations/components/FormPartialPayeeQboMapping";
import { useCreateContractorBulkAction } from "../../hooks/useCreateContractorBulkAction";
import { CreatePayeeInput, useCreatePayee } from "../../hooks/useCreatePayee";
import { routeHelpersPayeeDetails } from "../../utils";
import { FieldEngagementIds } from "./FieldEngagementIds";

export type CreateContractorModalProps = WSModalCustomProps<Payee | void> & {
  onSuccess?: (client?: Payee) => Promise<void>;
  redirectToDetails?: boolean;
};

type FormData = Partial<PayeeQboMappingFormValues> & {
  company: string;
  customFields?: Record<string, string | boolean>;
  email: string;
  engagementIds?: string[];
  externalId?: string;
  name: string;
};

export const CreateContractorModal: React.FC<CreateContractorModalProps> = ({
  onClose,
  onSuccess,
  redirectToDetails
}) => {
  const { openSnackbar } = useWSSnackbar();

  const goToPayeeDetails = routeHelpersPayeeDetails.useGoTo();

  const queryEngagements = queriesEngagements.listEngagements.useQueryAll(
    { filter: { status: { anyOf: [EngagementStatus.ACTIVE] } } },
    { refetchOnMount: false, retry: false }
  );

  // Engagements created from inside the form. The list query refetches after the
  // create, but the newly selected engagement has to be resolvable (for its
  // `engagementType`) the instant it is selected, whether or not that refetch has
  // landed.
  const [createdEngagements, setCreatedEngagements] = useState<Engagement[]>(
    []
  );

  const queryCustomFields = queriesCustomFields.listCustomFields.useQueryAll({
    filter: { resourceType: { eq: CustomFieldResourceType.PAYEE } }
  });
  const customFields = queryCustomFields.flatData;

  const [createPayee, createPayeeMeta] = useCreatePayee();
  const runCreateContractorBulkAction = useCreateContractorBulkAction();

  return (
    <WSModal title="Add contractor" size="S" onClose={onClose}>
      <WSQueries
        queries={{
          queryEngagements,
          queryCustomFields
        }}
      >
        {() => {
          const engagements = uniqBy(
            [
              ...(queryEngagements.flatData ?? []).filter(e => !e.isDefault),
              ...createdEngagements
            ],
            e => e.id
          );
          const engagementOptions = selectorEngagementOptions(engagements);
          return (
            <WSForm<FormData>
              defaultValues={{
                ...PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES,
                company: "",
                customFields: getFormDataCustomFields(customFields, {}),
                email: "",
                engagementIds: [] as string[],
                externalId: "",
                name: ""
              }}
              validationSchema={Yup.object().shape({
                company: contactCompanyValidator,
                customFields: getValidationSchemaCustomFields(customFields),
                email: validatorEmail.required("Required"),
                // v3 has no implicit default engagement to fall back on: a
                // contractor with no engagement can't be paid, so the choice is
                // made here instead of being discovered later.
                engagementIds: Yup.array()
                  .of(Yup.string())
                  .min(1, "Select at least one engagement"),
                name: contactNameValidator
              })}
              onSubmit={async data => {
                const selectedEngagementIds = data.engagementIds || [];
                const isSingleSelection = selectedEngagementIds.length <= 1;

                // Payer-owned data (the QBO mapping) has no home on v3
                // `PayeeCreate`; it's bridged through `updatePayee` inside
                // `useCreatePayee`. Skip it when empty.
                const payerOwnedData: CreatePayeeInput["payerOwnedData"] = {};

                const customFieldValues =
                  customFields.length > 0
                    ? selectorPayeeCustomFieldsRequest({
                        fields: customFields,
                        values: data.customFields ?? {}
                      })
                    : undefined;

                if (isSingleSelection) {
                  // `FormPartialPayeeQboMapping` renders nothing unless the
                  // account has a usable QuickBooks connection, so these stay at
                  // their empty default when there is no mapping to send.
                  if (data.qboVendorId || data.qboExpenseAccountId) {
                    payerOwnedData.integration = {
                      quickbooks: {
                        expenseAccountId: data.qboExpenseAccountId,
                        vendorId: data.qboVendorId
                      }
                    };
                  }

                  const result = await createPayee({
                    email: data.email,
                    name: data.name,
                    company: data.company,
                    externalId: data.externalId,
                    customFields: customFieldValues,
                    payerOwnedData,
                    engagements: selectedEngagementIds.map(id => ({
                      id,
                      engagementType:
                        engagements.find(e => e.id === id)?.engagementType ??
                        PayeeEngagementType.CONTRACTOR
                    }))
                  });

                  if (!result) return;
                  await onSuccess?.(result);
                  onClose(result);
                  redirectToDetails &&
                    goToPayeeDetails({ payeeId: result.id! });
                  openSnackbar({
                    message: "Contractor added successfully",
                    type: "success"
                  });
                } else {
                  const result = await runCreateContractorBulkAction(
                    {
                      payeeCreationData: {
                        email: data.email,
                        name: data.name,
                        company: data.company,
                        externalId: data.externalId,
                        customFields: customFieldValues,
                        payerOwnedData
                      },
                      engagements: selectedEngagementIds.map(id => ({
                        id,
                        name:
                          engagementOptions.find(o => o.value === id)?.label ||
                          id,
                        engagementType:
                          engagements.find(e => e.id === id)?.engagementType ??
                          PayeeEngagementType.CONTRACTOR
                      }))
                    },
                    {
                      toDetailsOnSuccess: !!redirectToDetails
                    }
                  );

                  if (!result.payee) return;
                  await onSuccess?.(result.payee);
                  onClose(result.payee);
                  result.toDetails &&
                    goToPayeeDetails({ payeeId: result.payee.id! });
                }
              }}
            >
              <WSList gap="L">
                <WSInfoBox>
                  After adding a contractor, they will be invited by the email
                  provided to sign-up for a Wingspan account in order to receive
                  payments from you.
                </WSInfoBox>

                <WSForm.Field
                  name="email"
                  label="Contractor email *"
                  component={WSInputText}
                  componentProps={{
                    helperText: "Contractor’s personal or business email",
                    tooltip: {
                      dark: true,
                      title: "Contractor email (required)",
                      children: `Send an invite to the contractor’s personal email or the email of the contractor’s business that they operate under.

We do not recommend using emails provided by your company since the contractor’s account is portable for their own use as a contractor.`
                    },
                    placeholder: "Contractor email"
                  }}
                />
                <WSForm.Field
                  name="company"
                  label="Contractor company"
                  component={WSInputText}
                  componentProps={{
                    tooltip: {
                      dark: true,
                      title: "Contractor company (optional)",
                      children: `If the contractor operates under their own contractor business name, enter it here. This information will be confirmed by the contractor when they onboard.`
                    },
                    placeholder: "Contractor company"
                  }}
                />
                <WSForm.Field
                  name="name"
                  label="Contractor full name"
                  component={WSInputText}
                  componentProps={{
                    tooltip: {
                      dark: true,
                      title: "Contractor full name (optional)",
                      children: `This information will be confirmed by the contractor when they onboard.`
                    },
                    placeholder: "Contractor full name"
                  }}
                />
                <WSDivider my="S" />
                <WSForm.Field
                  name="externalId"
                  label="External ID"
                  component={WSInputText}
                  componentProps={{
                    placeholder: "External ID"
                  }}
                />
                {customFields.length > 0 ? (
                  <>
                    <WSDivider my="S" />
                    {customFields.map(customField => {
                      const key = customField.key ?? "";

                      return customField.dataType ===
                        AttributeDataType.BOOLEAN ? (
                        <WSForm.Field
                          key={key}
                          name={`customFields.${key}`}
                          component={WSControl}
                          componentProps={{
                            type: "checkbox",
                            label: customField.name
                          }}
                        />
                      ) : (
                        <WSForm.Field
                          key={key}
                          name={`customFields.${key}`}
                          label={`${customField.name}${
                            customField.isRequired ? "" : " (optional)"
                          }`}
                          component={WSInputText}
                          componentProps={{ placeholder: customField.name }}
                        />
                      );
                    })}
                  </>
                ) : null}

                <WSDivider my="S" />
                <FieldEngagementIds
                  options={engagementOptions}
                  onEngagementCreated={engagement =>
                    setCreatedEngagements(current => [...current, engagement])
                  }
                />

                <WSForm.Value name="engagementIds">
                  {(engagementIds: string[] | null) => (
                    <FormPartialPayeeQboMapping
                      disabled={(engagementIds || [])?.length > 1}
                    />
                  )}
                </WSForm.Value>

                <ErrorAlert
                  error={createPayeeMeta.error}
                  context="CreatePayee"
                />

                <WSActions
                  mt="XL"
                  alignment="fill"
                  buttons={[
                    {
                      label: "Add contractor",
                      kind: "Primary",
                      type: "submit",
                      loading: createPayeeMeta.isLoading
                    },
                    {
                      label: "Cancel",
                      kind: "Secondary",
                      type: "button",
                      onClick() {
                        onClose();
                      }
                    }
                  ]}
                />
              </WSList>
            </WSForm>
          );
        }}
      </WSQueries>
    </WSModal>
  );
};
